import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token storage key
const TOKEN_KEY = 'qr_access_token';

// Secure token storage helpers - sessionStorage only (cleared on tab close)
// This prevents XSS attacks from accessing tokens across sessions
// For maximum security in production, consider httpOnly cookies via backend
const tokenStorage = {
  get: () => {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token) => {
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
    } catch {
      // Storage unavailable
    }
  },
  remove: () => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      // Storage unavailable
    }
  }
};

// Configure axios interceptor to always use the latest token
axios.interceptors.request.use(
  (config) => {
    const token = tokenStorage.get();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => tokenStorage.get());
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    tokenStorage.remove();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    toast.info('Sesión cerrada');
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const currentToken = tokenStorage.get();
      if (!currentToken) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Only logout if token is truly invalid (401), not for network errors
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchCurrentUser]);

  const login = useCallback(async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      const { access_token, user: userData } = response.data;
      tokenStorage.set(access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success(`Bienvenido ${userData.username}`);
      return { success: true, user: userData };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al iniciar sesión';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const register = useCallback(async (username, password, department) => {
    try {
      await axios.post(`${API}/auth/register`, {
        username,
        password,
        department
      });
      
      toast.success('Usuario registrado exitosamente');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al registrar usuario';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout
  }), [user, token, loading, login, register, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
